/**
 * PlaybackManager — Système de tutoriel interactif avec démo animée.
 * Le jeu tourne RÉELLEMENT en arrière-plan (plein écran, sans voile).
 * Un curseur fantôme interagit avec le jeu en dispatchant de vrais événements.
 * Les instructions sont affichées dans un panneau semi-transparent par-dessus.
 *
 * Ne contient AUCUNE logique spécifique à un mini-jeu.
 */
export default class PlaybackManager {
    constructor(engine) {
        this.engine = engine;
        this.i18n = engine.i18n;
        this._overlay = null;
        this._playbackContainer = null;
        this._ghostCursor = null;
        this._animationFrame = null;
        this._timerInterval = null;
        this._actionTimeout = null;
        this._currentGame = null;
        this._loopTimeout = null;
        this._onResume = null;
        this._active = false;
        this._fakeInput = null;
        this._onViewportChange = null;

        // Default timer per mode (seconds)
        this.timerDefaults = {
            'INFINITE': 10,
            'STANDARD': 10,
            'ALL_GAMES': 8,
            'CHALLENGE': 5
        };
    }

    /**
     * Show a playback tutorial for the given game class.
     * @param {Function} GameClass - The microgame class
     * @param {Function} onResume - Callback when tutorial finishes (skip or timer)
     * @param {Object} options - { gameMode, hideClose, timerOverride, isPreview, showDontShowAgain, disableStorageKey }
     * @returns {boolean} true if playback shown, false if no playback available (use fallback)
     */
    show(GameClass, onResume, options = {}) {
        // Check if game has playback data
        if (typeof GameClass.getPlayback !== 'function') {
            return false; // Signal caller to use fallback
        }

        const playbackData = GameClass.getPlayback();
        if (!playbackData) {
            return false;
        }

        this.hide(); // Clean up any existing playback

        this._onResume = onResume;
        this._active = true;

        const gameMode = options.gameMode || this.engine.gameMode || 'INFINITE';
        const timerDuration = options.timerOverride || this.timerDefaults[gameMode] || 10;

        const lang = this.i18n.getLanguage();
        const getText = (obj) => {
            if (!obj) return '';
            if (typeof obj === 'string') return obj;
            return obj[lang] || obj['en'] || obj['fr'] || '';
        };

        // ========== BUILD DOM ==========

        // Main overlay (fullscreen, transparent — game is visible)
        const overlay = document.createElement('div');
        overlay.className = 'playback-overlay';
        overlay.id = 'playback-overlay';

        // --- Playback game container (FULL VISIBILITY — no grey veil) ---
        const gameContainer = document.createElement('div');
        gameContainer.className = 'playback-game-container';
        gameContainer.id = 'playback-game-container';
        overlay.appendChild(gameContainer);

        // --- Ghost cursor ---
        const ghostCursor = document.createElement('div');
        ghostCursor.className = 'playback-ghost-cursor';
        ghostCursor.id = 'playback-ghost-cursor';
        const cursorInner = document.createElement('div');
        cursorInner.className = 'playback-cursor-inner';
        ghostCursor.appendChild(cursorInner);
        const cursorTrail = document.createElement('div');
        cursorTrail.className = 'playback-cursor-trail';
        ghostCursor.appendChild(cursorTrail);
        overlay.appendChild(ghostCursor);

        // --- Instructions overlay (with background only around text) ---
        const instructionsPanel = document.createElement('div');
        instructionsPanel.className = 'playback-instructions';

        // Game title
        const titleEl = document.createElement('div');
        titleEl.className = 'playback-title';
        titleEl.textContent = getText(playbackData.title) || GameClass.name || '';
        instructionsPanel.appendChild(titleEl);

        // Objective text
        if (playbackData.objective) {
            const objectiveEl = document.createElement('div');
            objectiveEl.className = 'playback-objective';
            objectiveEl.textContent = getText(playbackData.objective);
            instructionsPanel.appendChild(objectiveEl);
        }

        // Controls text
        if (playbackData.controls) {
            const controlsEl = document.createElement('div');
            controlsEl.className = 'playback-controls';
            controlsEl.textContent = getText(playbackData.controls);
            instructionsPanel.appendChild(controlsEl);
        }

        overlay.appendChild(instructionsPanel);

        // --- Bottom bar: Timer + Skip button ---
        const bottomBar = document.createElement('div');
        bottomBar.className = 'playback-bottom-bar';

        // Circular timer
        const timerContainer = document.createElement('div');
        timerContainer.className = 'playback-timer-container';

        const timerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        timerSvg.setAttribute('class', 'playback-timer-svg');
        timerSvg.setAttribute('viewBox', '0 0 44 44');

        const timerBg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        timerBg.setAttribute('class', 'playback-timer-bg');
        timerBg.setAttribute('cx', '22');
        timerBg.setAttribute('cy', '22');
        timerBg.setAttribute('r', '20');
        timerSvg.appendChild(timerBg);

        const timerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        timerCircle.setAttribute('class', 'playback-timer-circle');
        timerCircle.setAttribute('cx', '22');
        timerCircle.setAttribute('cy', '22');
        timerCircle.setAttribute('r', '20');
        timerCircle.id = 'playback-timer-circle';
        const circumference = 2 * Math.PI * 20;
        timerCircle.style.strokeDasharray = `${circumference}`;
        timerCircle.style.strokeDashoffset = '0';
        timerSvg.appendChild(timerCircle);

        timerContainer.appendChild(timerSvg);

        const timerText = document.createElement('div');
        timerText.className = 'playback-timer-text';
        timerText.id = 'playback-timer-text';
        timerText.textContent = timerDuration;
        timerContainer.appendChild(timerText);

        bottomBar.appendChild(timerContainer);

        // Skip button
        const skipBtn = document.createElement('button');
        skipBtn.className = 'neon-btn playback-skip-btn';
        skipBtn.id = 'playback-skip-btn';
        const t = (key) => this.i18n.t(`tutorial.${key}`);
        if (options.isPreview) {
            skipBtn.textContent = 'OK';
        } else {
            skipBtn.textContent = t('play') || 'PLAY';
        }
        skipBtn.addEventListener('click', () => this._resume());
        bottomBar.appendChild(skipBtn);

        if (!options.isPreview && options.showDontShowAgain && options.disableStorageKey) {
            const dontShowBtn = document.createElement('button');
            dontShowBtn.className = 'neon-btn small playback-disable-btn';
            dontShowBtn.id = 'playback-disable-btn';
            dontShowBtn.textContent = t('dontShowAgain') || "DON'T SHOW AGAIN";
            dontShowBtn.addEventListener('click', () => {
                localStorage.setItem(options.disableStorageKey, 'true');
                this._resume();
            });
            bottomBar.appendChild(dontShowBtn);
        }

        overlay.appendChild(bottomBar);

        // ========== MOUNT ==========
        document.body.appendChild(overlay);
        this._bindOverlayToGameContainer(overlay);
        this._overlay = overlay;
        this._playbackContainer = gameContainer;
        this._ghostCursor = ghostCursor;

        // Animate in
        requestAnimationFrame(() => {
            overlay.classList.add('active');
        });

        // ========== START PLAYBACK GAME ==========
        this._startPlaybackGame(GameClass, gameContainer, playbackData);

        // ========== START COUNTDOWN ==========
        this._startCountdown(timerDuration, circumference, timerCircle, timerText);

        return true;
    }

    /**
     * Keep playback overlay aligned with the mobile-sized game container.
     */
    _bindOverlayToGameContainer(overlay) {
        const applyBounds = () => {
            const gameRoot = document.getElementById('game-container');
            if (!gameRoot) {
                overlay.style.left = '0px';
                overlay.style.top = '0px';
                overlay.style.width = '100vw';
                overlay.style.height = '100vh';
                return;
            }

            const rect = gameRoot.getBoundingClientRect();
            overlay.style.left = `${rect.left}px`;
            overlay.style.top = `${rect.top}px`;
            overlay.style.width = `${rect.width}px`;
            overlay.style.height = `${rect.height}px`;
        };

        applyBounds();
        this._onViewportChange = applyBounds;
        window.addEventListener('resize', this._onViewportChange);
        window.addEventListener('scroll', this._onViewportChange, { passive: true });
    }

    /**
     * Start the game in playback (read-only) mode inside the container.
     * The game runs for real but win/lose are intercepted to restart.
     */
    _startPlaybackGame(GameClass, container, playbackData) {
        try {
            const fakeEngine = this._createFakeEngine(container);
            const game = new GameClass(fakeEngine);
            game.isPlayback = true;

            // Override win/lose to restart the game automatically
            game.win = () => {
                if (!this._active) return;
                setTimeout(() => this._restartPlaybackGame(container), 600);
            };
            game.lose = () => {
                if (!this._active) return;
                setTimeout(() => this._restartPlaybackGame(container), 600);
            };

            game.start();
            this._currentGame = game;

            // Run the game update loop
            let lastTime = performance.now();
            const updateLoop = (time) => {
                if (!this._active) return;
                const dt = time - lastTime;
                lastTime = time;
                try {
                    if (game.update && game.active) {
                        game.update(Math.min(dt, 50));
                    }
                } catch (e) {
                    // Silently ignore errors in playback mode
                }
                this._animationFrame = requestAnimationFrame(updateLoop);
            };
            this._animationFrame = requestAnimationFrame(updateLoop);

            // Start the smart ghost cursor after a brief delay (let game render)
            setTimeout(() => {
                if (this._active) {
                    if (Array.isArray(playbackData.actions) && playbackData.actions.length > 0) {
                        this._startScriptedGhost(game, container, playbackData);
                    } else {
                        this._startSmartGhost(game, container, playbackData);
                    }
                }
            }, 400);

        } catch (e) {
            console.warn('[PlaybackManager] Failed to start playback game:', e);
        }
    }

    /**
     * Create a fake engine proxy with a REAL input system
     * so we can dispatch tap/swipe events to the game.
     */
    _createFakeEngine(container) {
        const realEngine = this.engine;

        const fakeInstruction = document.createElement('div');
        fakeInstruction.style.display = 'none';

        const fakeTimerParent = document.createElement('div');
        const fakeTimerFill = document.createElement('div');
        fakeTimerParent.appendChild(fakeTimerFill);

        // Build a lightweight input system that we can emit events into
        const fakeInput = {
            callbacks: {},
            element: container,
            on(event, cb) {
                if (!this.callbacks[event]) this.callbacks[event] = [];
                this.callbacks[event].push(cb);
            },
            off(event, cb) {
                if (!this.callbacks[event]) return;
                if (cb) {
                    this.callbacks[event] = this.callbacks[event].filter(c => c !== cb);
                } else {
                    this.callbacks[event] = [];
                }
            },
            emit(event, data) {
                if (this.callbacks[event]) {
                    this.callbacks[event].forEach(c => c(data));
                }
            }
        };
        this._fakeInput = fakeInput;

        return {
            container: container,
            ui: {
                instruction: fakeInstruction,
                timerFill: fakeTimerFill,
                score: document.createElement('div'),
                lives: document.createElement('div')
            },
            input: fakeInput,
            speedMultiplier: 1.0,
            maxTime: 5000,
            timer: 5000,
            gameMode: 'PLAYBACK',
            isChallengeMode: false,
            activeChallenge: null,
            i18n: realEngine.i18n,
            authManager: { currentUser: null, isGameUnlocked: () => true },
            handleMicrogameResult: () => {},
            updateHUD: () => {},
            showNotification: () => {}
        };
    }

    /**
     * Smart ghost cursor: reads the game DOM to find interactive elements
     * and moves the cursor to them, then dispatches real input events.
     */
    _startSmartGhost(game, container, playbackData) {
        if (!this._active) return;

        const cursor = this._ghostCursor;
        if (!cursor) return;

        // Position cursor at center initially
        cursor.style.left = '50%';
        cursor.style.top = '50%';
        cursor.style.opacity = '1';

        const runLoop = () => {
            if (!this._active || !this._currentGame) return;

            // Find the interactive target in the game DOM
            const target = this._findGameTarget(container);

            if (target) {
                const rect = target.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                // Calculate target center as percentage of overlay
                const targetX = ((rect.left + rect.width / 2 - containerRect.left) / containerRect.width) * 100;
                const targetY = ((rect.top + rect.height / 2 - containerRect.top) / containerRect.height) * 100;

                // Move cursor to target
                this._animateMove(cursor, targetX, targetY, 400, () => {
                    if (!this._active) return;

                    // Pulse the cursor
                    cursor.classList.add('tapping');

                    // Dispatch a real tap event to the game's input system
                    const tapX = rect.left + rect.width / 2;
                    const tapY = rect.top + rect.height / 2;
                    if (this._fakeInput) {
                        this._fakeInput.emit('tap', { x: tapX, y: tapY });
                    }
                    this._dispatchDomTap(target, tapX, tapY);

                    setTimeout(() => {
                        if (!this._active) return;
                        cursor.classList.remove('tapping');

                        // Wait then loop
                        this._loopTimeout = setTimeout(() => runLoop(), 800);
                    }, 350);
                });
            } else {
                // No target found, wait and retry
                this._loopTimeout = setTimeout(() => runLoop(), 500);
            }
        };

        runLoop();
    }

    /**
     * Scripted ghost cursor for deterministic tutorials.
     * Supports absolute moves/taps and selector-based target taps.
     */
    _startScriptedGhost(game, container, playbackData) {
        if (!this._active) return;
        const cursor = this._ghostCursor;
        if (!cursor) return;

        const actions = Array.isArray(playbackData.actions) ? playbackData.actions : [];
        if (!actions.length) {
            this._startSmartGhost(game, container, playbackData);
            return;
        }

        cursor.style.left = '50%';
        cursor.style.top = '50%';
        cursor.style.opacity = '1';

        const runActionAt = (index) => {
            if (!this._active) return;
            const action = actions[index % actions.length];
            if (!action) return;

            const next = () => {
                this._loopTimeout = setTimeout(() => runActionAt(index + 1), 30);
            };

            if (action.type === 'wait') {
                this._loopTimeout = setTimeout(() => runActionAt(index + 1), action.duration || 400);
                return;
            }

            if (action.type === 'move') {
                this._animateMove(
                    cursor,
                    action.x ?? 50,
                    action.y ?? 50,
                    action.duration || 350,
                    () => {
                        if (!this._active) return;
                        const rect = container.getBoundingClientRect();
                        const moveX = rect.left + ((action.x ?? 50) / 100) * rect.width;
                        const moveY = rect.top + ((action.y ?? 50) / 100) * rect.height;
                        this._dispatchPointerEvent(container, 'pointermove', moveX, moveY);
                        this._dispatchMouseEvent(container, 'mousemove', moveX, moveY);
                        this._dispatchTouchEvent(container, 'touchmove', moveX, moveY);
                        next();
                    }
                );
                return;
            }

            if (action.type === 'tap') {
                const x = action.x ?? 50;
                const y = action.y ?? 50;
                this._animateMove(cursor, x, y, action.duration || 280, () => {
                    if (!this._active) return;
                    cursor.classList.add('tapping');
                    const rect = container.getBoundingClientRect();
                    const tapX = rect.left + (x / 100) * rect.width;
                    const tapY = rect.top + (y / 100) * rect.height;
                    if (this._fakeInput) {
                        this._fakeInput.emit('tap', {
                            x: tapX,
                            y: tapY
                        });
                    }
                    this._dispatchDomTap(container, tapX, tapY);
                    setTimeout(() => {
                        if (!this._active) return;
                        cursor.classList.remove('tapping');
                        next();
                    }, 260);
                });
                return;
            }

            if (action.type === 'tapTarget') {
                const selector = action.selector || '.tap-target';
                const target = container.querySelector(selector);
                if (!target) {
                    this._loopTimeout = setTimeout(() => runActionAt(index), 140);
                    return;
                }

                const rect = target.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const x = ((rect.left + rect.width / 2 - containerRect.left) / containerRect.width) * 100;
                const y = ((rect.top + rect.height / 2 - containerRect.top) / containerRect.height) * 100;

                this._animateMove(cursor, x, y, action.duration || 320, () => {
                    if (!this._active) return;
                    cursor.classList.add('tapping');
                    const tapX = rect.left + rect.width / 2;
                    const tapY = rect.top + rect.height / 2;
                    if (this._fakeInput) {
                        this._fakeInput.emit('tap', {
                            x: tapX,
                            y: tapY
                        });
                    }
                    this._dispatchDomTap(target, tapX, tapY);
                    setTimeout(() => {
                        if (!this._active) return;
                        cursor.classList.remove('tapping');
                        next();
                    }, 240);
                });
                return;
            }

            if (action.type === 'swipe') {
                const fromX = action.fromX ?? 30;
                const fromY = action.fromY ?? 50;
                const toX = action.toX ?? 70;
                const toY = action.toY ?? 50;
                this._animateMove(cursor, fromX, fromY, 220, () => {
                    if (!this._active) return;
                    cursor.classList.add('swiping');
                    const rect = container.getBoundingClientRect();
                    const startX = rect.left + (fromX / 100) * rect.width;
                    const startY = rect.top + (fromY / 100) * rect.height;
                    const endX = rect.left + (toX / 100) * rect.width;
                    const endY = rect.top + (toY / 100) * rect.height;
                    this._animateMove(cursor, toX, toY, action.duration || 420, () => {
                        if (!this._active) return;
                        if (this._fakeInput) {
                            this._fakeInput.emit('swipe', { startX, startY, endX, endY, dx: endX - startX, dy: endY - startY });
                        }
                        this._dispatchDomSwipe(container, startX, startY, endX, endY);
                        setTimeout(() => {
                            if (!this._active) return;
                            cursor.classList.remove('swiping');
                            next();
                        }, 200);
                    });
                });
                return;
            }

            if (action.type === 'hold') {
                const x = action.x ?? 50;
                const y = action.y ?? 50;
                const holdDuration = action.holdDuration || 700;
                this._animateMove(cursor, x, y, action.duration || 240, () => {
                    if (!this._active) return;
                    cursor.classList.add('holding');
                    const rect = container.getBoundingClientRect();
                    const holdX = rect.left + (x / 100) * rect.width;
                    const holdY = rect.top + (y / 100) * rect.height;
                    if (this._fakeInput) {
                        this._fakeInput.emit('holdStart', { x: holdX, y: holdY });
                    }
                    this._dispatchDomPressStart(container, holdX, holdY);
                    this._loopTimeout = setTimeout(() => {
                        if (!this._active) return;
                        if (this._fakeInput) {
                            this._fakeInput.emit('holdEnd', { x: holdX, y: holdY });
                        }
                        this._dispatchDomPressEnd(container, holdX, holdY);
                        cursor.classList.remove('holding');
                        next();
                    }, holdDuration);
                });
                return;
            }

            next();
        };

        runActionAt(0);
    }

    /**
     * Find the main interactive target in the game container.
     * Looks for common game element patterns.
     */
    _findGameTarget(container) {
        // Priority list of selectors for common game targets
        const selectors = [
            '.tap-target',
            '.game-target',
            '.target',
            '.ball',
            '.item',
            '.clickable',
            '[data-target]',
            'button:not(.playback-skip-btn)',
            '.option',
            '.card'
        ];

        for (const sel of selectors) {
            const el = container.querySelector(sel);
            if (el && el.offsetParent !== null) {
                return el;
            }
        }

        // Fallback: find the first visible child element with a size
        const children = container.querySelectorAll('div, span, canvas');
        for (const child of children) {
            const rect = child.getBoundingClientRect();
            if (rect.width > 10 && rect.height > 10 && rect.width < 300) {
                return child;
            }
        }

        return null;
    }

    /**
     * Restart the playback game (called when game wins/loses in demo).
     */
    _restartPlaybackGame(container) {
        if (!this._active || !this._currentGame) return;

        // Stop current animation frame
        if (this._animationFrame) {
            cancelAnimationFrame(this._animationFrame);
            this._animationFrame = null;
        }
        // Stop ghost loop
        if (this._loopTimeout) {
            clearTimeout(this._loopTimeout);
            this._loopTimeout = null;
        }
        if (this._actionTimeout) {
            clearTimeout(this._actionTimeout);
            this._actionTimeout = null;
        }

        try {
            this._currentGame.cleanup();
        } catch (e) {}

        container.innerHTML = '';

        try {
            const GameClass = this._currentGame.constructor;
            const playbackData = GameClass.getPlayback();
            const fakeEngine = this._createFakeEngine(container);
            const game = new GameClass(fakeEngine);
            game.isPlayback = true;
            game.win = () => {
                if (!this._active) return;
                setTimeout(() => this._restartPlaybackGame(container), 600);
            };
            game.lose = () => {
                if (!this._active) return;
                setTimeout(() => this._restartPlaybackGame(container), 600);
            };
            game.start();
            this._currentGame = game;

            // Restart update loop
            let lastTime = performance.now();
            const updateLoop = (time) => {
                if (!this._active) return;
                const dt = time - lastTime;
                lastTime = time;
                try {
                    if (game.update && game.active) {
                        game.update(Math.min(dt, 50));
                    }
                } catch (e) {}
                this._animationFrame = requestAnimationFrame(updateLoop);
            };
            this._animationFrame = requestAnimationFrame(updateLoop);

            // Restart smart ghost
            setTimeout(() => {
                if (this._active) {
                    if (Array.isArray(playbackData.actions) && playbackData.actions.length > 0) {
                        this._startScriptedGhost(game, container, playbackData);
                    } else {
                        this._startSmartGhost(game, container, playbackData);
                    }
                }
            }, 400);
        } catch (e) {
            console.warn('[PlaybackManager] Failed to restart playback game:', e);
        }
    }

    /**
     * Smoothly animate cursor movement using CSS transitions.
     */
    _animateMove(cursor, targetX, targetY, duration, onComplete) {
        cursor.style.transition = `left ${duration}ms ease-out, top ${duration}ms ease-out`;
        cursor.style.left = `${targetX}%`;
        cursor.style.top = `${targetY}%`;

        this._actionTimeout = setTimeout(() => {
            cursor.style.transition = 'none';
            if (onComplete) onComplete();
        }, duration);
    }

    /**
     * Dispatch pointer/mouse/touch tap sequence on a target.
     */
    _dispatchDomTap(target, x, y) {
        if (!target || typeof target.dispatchEvent !== 'function') return;
        this._dispatchDomPressStart(target, x, y);
        this._dispatchDomPressEnd(target, x, y);
        this._dispatchMouseEvent(target, 'click', x, y);
    }

    _dispatchDomPressStart(target, x, y) {
        this._dispatchPointerEvent(target, 'pointerdown', x, y);
        this._dispatchMouseEvent(target, 'mousedown', x, y);
        this._dispatchTouchEvent(target, 'touchstart', x, y);
    }

    _dispatchDomPressEnd(target, x, y) {
        this._dispatchPointerEvent(target, 'pointerup', x, y);
        this._dispatchMouseEvent(target, 'mouseup', x, y);
        this._dispatchTouchEvent(target, 'touchend', x, y);
    }

    _dispatchDomSwipe(target, startX, startY, endX, endY) {
        if (!target || typeof target.dispatchEvent !== 'function') return;
        this._dispatchPointerEvent(target, 'pointerdown', startX, startY);
        this._dispatchMouseEvent(target, 'mousedown', startX, startY);
        this._dispatchTouchEvent(target, 'touchstart', startX, startY);

        const steps = 4;
        for (let i = 1; i <= steps; i++) {
            const t = i / steps;
            const x = startX + (endX - startX) * t;
            const y = startY + (endY - startY) * t;
            this._dispatchPointerEvent(target, 'pointermove', x, y);
            this._dispatchMouseEvent(target, 'mousemove', x, y);
            this._dispatchTouchEvent(target, 'touchmove', x, y);
        }

        this._dispatchPointerEvent(target, 'pointerup', endX, endY);
        this._dispatchMouseEvent(target, 'mouseup', endX, endY);
        this._dispatchTouchEvent(target, 'touchend', endX, endY);
    }

    _dispatchPointerEvent(target, type, x, y) {
        if (typeof PointerEvent === 'undefined') return;
        target.dispatchEvent(new PointerEvent(type, {
            bubbles: true,
            cancelable: true,
            pointerType: 'touch',
            clientX: x,
            clientY: y
        }));
    }

    _dispatchMouseEvent(target, type, x, y) {
        target.dispatchEvent(new MouseEvent(type, {
            bubbles: true,
            cancelable: true,
            clientX: x,
            clientY: y
        }));
    }

    _dispatchTouchEvent(target, type, x, y) {
        if (typeof TouchEvent === 'undefined' || typeof Touch === 'undefined') return;
        const touch = new Touch({
            identifier: Date.now(),
            target,
            clientX: x,
            clientY: y,
            radiusX: 8,
            radiusY: 8
        });
        const touches = type === 'touchend' ? [] : [touch];
        target.dispatchEvent(new TouchEvent(type, {
            bubbles: true,
            cancelable: true,
            touches,
            targetTouches: touches,
            changedTouches: [touch]
        }));
    }

    /**
     * Start the countdown timer.
     */
    _startCountdown(totalSeconds, circumference, timerCircle, timerText) {
        let remaining = totalSeconds;
        const startTime = Date.now();

        this._timerInterval = setInterval(() => {
            if (!this._active) {
                clearInterval(this._timerInterval);
                return;
            }

            const elapsed = (Date.now() - startTime) / 1000;
            remaining = Math.max(0, totalSeconds - elapsed);

            const progress = remaining / totalSeconds;
            const offset = circumference * (1 - progress);
            timerCircle.style.strokeDashoffset = `${offset}`;
            timerText.textContent = Math.ceil(remaining);

            if (remaining <= 3) {
                timerCircle.style.stroke = 'var(--secondary-color)';
                timerText.style.color = 'var(--secondary-color)';
            }

            if (remaining <= 0) {
                clearInterval(this._timerInterval);
                this._resume();
            }
        }, 50);
    }

    /**
     * Resume the game (called by skip button or timer expiry).
     */
    _resume() {
        if (!this._active) return;

        const onResume = this._onResume;
        this.hide();

        if (onResume) {
            onResume();
        }
    }

    /**
     * Clean up and hide the playback overlay.
     */
    hide() {
        this._active = false;

        if (this._animationFrame) {
            cancelAnimationFrame(this._animationFrame);
            this._animationFrame = null;
        }

        if (this._timerInterval) {
            clearInterval(this._timerInterval);
            this._timerInterval = null;
        }

        if (this._actionTimeout) {
            clearTimeout(this._actionTimeout);
            this._actionTimeout = null;
        }

        if (this._loopTimeout) {
            clearTimeout(this._loopTimeout);
            this._loopTimeout = null;
        }

        if (this._currentGame) {
            try {
                this._currentGame.cleanup();
            } catch (e) {}
            this._currentGame = null;
        }

        if (this._overlay) {
            this._overlay.classList.remove('active');
            this._overlay.classList.add('closing');
            const el = this._overlay;
            setTimeout(() => {
                if (el && el.parentNode) el.remove();
            }, 300);
            this._overlay = null;
        }

        if (this._onViewportChange) {
            window.removeEventListener('resize', this._onViewportChange);
            window.removeEventListener('scroll', this._onViewportChange);
            this._onViewportChange = null;
        }

        this._ghostCursor = null;
        this._playbackContainer = null;
        this._fakeInput = null;
        this._onResume = null;
    }
}
